import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { format } from 'date-fns';
import moment from 'moment-timezone';
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import PopUp from 'src/components/design-system/PopUp';
import { isValidDate } from 'src/utils/formatTime';
import { errorIcon, getBgColor } from 'src/utils/icons';
import { convertDate } from 'src/utils/transformation';
import OcrIndicator from '../split_line_item/OcrIndicator';
import CustomDateInput from './CustomDateInput';

import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';

const InvoiceDate = () => {
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const handleInputChange = useContextSelector(InvoiceContext, (value) => value.handleInputChange);
  const ocrResponse = useContextSelector(InvoiceContext, (value) => value.ocrResponse);
  const initialInvoice = useContextSelector(InvoiceContext, (value) => value.initialInvoice);
  const handleHoverOcrField = useContextSelector(
    InvoiceContext,
    (value) => value.handleHoverOcrField
  );
  const title = useContextSelector(InvoiceContext, (value) => value.title);
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const errors = useContextSelector(InvoiceContext, (value) => value.errors);
  const containerRef = useRef<HTMLSpanElement>(null);

  const formatDate = useMemo(
    () =>
      invoice?.invoiceDate !== null
        ? format(convertDate(invoice?.invoiceDate), 'MM/dd/yyyy')
        : undefined,
    [invoice?.invoiceDate]
  );

  const hasError = useMemo(() => !!errors?.invoiceDate?.length, [errors?.invoiceDate?.length]);
  const preventDefault = useCallback((e: any) => {
    e.preventDefault();
  }, []);

  const id = useId();

  useEffect(() => {
    document.getElementById(id)?.addEventListener('keyup', (e: any) => {
      const { value } = e.target;
      const newValue = value === 'month/day/year' ? '' : value;
      handleInputChange('_invoiceDate')({
        value: newValue as string,
      } as unknown as DatePickerChangeEvent);
    });
    return document.getElementById(id)?.removeEventListener('keyup', (e) => {});
  }, [handleInputChange, id]);

  const handleInvoiceDateChange = useCallback(
    (date: Date | null) => {
      handleInputChange('invoiceDate')({
        value: date,
      } as unknown as DatePickerChangeEvent);
      if (date) {
        handleInputChange('_invoiceDate')({
          value: moment(date).format('M/DD/YYYY'),
        } as unknown as DatePickerChangeEvent);
      }
    },
    [handleInputChange]
  );

  const ocrFormattedDate = useMemo(
    () =>
      ocrResponse.invoiceDate?.value
        ? isValidDate(ocrResponse.invoiceDate?.value)
          ? format(convertDate(ocrResponse.invoiceDate?.value), 'MM/dd/yyyy')
          : ocrResponse.invoiceDate?.value
        : undefined,
    [ocrResponse.invoiceDate?.value]
  );

  useEffect(() => {
    if (containerRef.current) {
      const inputElement = containerRef.current.querySelector('input');
      if (inputElement) {
        inputElement.setAttribute('ocr-data', 'invoiceDate');
      }
    }
  }, [containerRef]);

  const isEdited = useMemo(() => ocrFormattedDate !== formatDate, [formatDate, ocrFormattedDate]);
  return (
    <PopUp
      content={
        <div className="m-1 max-w-[300px]">
          <PopUpContentWrapper type="Error" contents={errors?.invoiceDate} />
        </div>
      }
      show={hasError}
      popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
      anchorAlign={{ horizontal: 'right', vertical: 'top' }}
    >
      <div className={`p-1 ${getBgColor(errors?.invoiceDate)} text-[13px]`}>
        <p className="relative font-bold pb-1 flex items-center">
          {title} Date {hasError && <span className="ml-1">{errorIcon}</span>}
          <div className="ml-auto">
            {initialInvoice?.ocrGenerated && ocrResponse.invoiceDate?.confidence && (
              <OcrIndicator
                isEdited={isEdited}
                ocrConfidence={ocrResponse.invoiceDate?.confidence}
                onHover={() => handleHoverOcrField('invoiceDate')}
              />
            )}
          </div>
        </p>

        {isInvoiceEditDisabled ? (
          <p title={formatDate}>{formatDate}</p>
        ) : (
          <span onDragStart={preventDefault} onDragOver={preventDefault} ref={containerRef}>
            <CustomDateInput
              title={formatDate}
              value={invoice?.invoiceDate || ''}
              valid={!hasError}
              id={id}
              onChange={handleInvoiceDateChange}
              onFocus={() => handleHoverOcrField('invoiceDate')} 
            />
          </span>
        )}
      </div>
    </PopUp>
  );
};

export default InvoiceDate;
